import { CoreButtonView, CoreTextView, type TCoreButtonStyleProps, type TCoreButtonViewProps, type TCoreTextStyleProps } from '@core/widgets';
import { useBehavior, useTriggerAction } from '@libs/core';
import { ActionActionTypeEnum, AssetsSvgMap, TWidgetProps } from '@libs/types';
import React from 'react';
import { getSemanticColors, memoByPalette, SemanticColor, TSemanticColors } from '../../theme/colors';
import { TInviteLinkData } from '../../types/data';
import { TInviteLinkOption } from '../../types/options';
import { TextStyle } from '../../types/styles';
import { VisualView } from '../Image/VisualView';

/** InviteLink palettes in the design of `colors`. */
const buildInviteLinkStyleProps = (colors: TSemanticColors) => {
    const container: TCoreButtonStyleProps = {
        main: colors[SemanticColor.FillTertiary],
        onMain: colors[SemanticColor.TextPrimary],
        outline: colors[SemanticColor.BorderPrimary],
        borderStroke: 1,
        borderRadius: 1000,
        padding: { top: 8, right: 24, bottom: 8, left: 24 },
        gap: 8,
        fillContainer: true,
        cursorDisabled: 'default',
        // Not in Figma: keeps the pill readable if the backend disables the row, instead of CoreButton's transparent / #999 defaults.
        disabled: {
            main: colors[SemanticColor.FillTertiaryDisabled],
            onMain: colors[SemanticColor.TextPrimaryDisabled],
            outline: colors[SemanticColor.BorderPrimaryDisabled],
        },
        hovered: {
            main: colors[SemanticColor.FillTertiaryHovered],
        },
        pressed: {
            main: colors[SemanticColor.FillTertiaryPressed],
        },
    };

    const value: TCoreTextStyleProps = {
        ...TextStyle.secondary,
        main: colors[SemanticColor.TextPrimary],
        textAlign: 'center',
        disabled: {
            main: colors[SemanticColor.TextPrimaryDisabled],
        },
    };

    return { container, value };
};
const getInviteLinkStyleProps = memoByPalette(buildInviteLinkStyleProps);

/**
 * Pill row showing a referral link; a tap runs the widget's onClick actions
 * (typically Player `copy` + `showToast`) with `data.value` as the copied text.
 */
export const InviteLinkWidget = ({ widget }: TWidgetProps<TInviteLinkData, TInviteLinkOption>) => {
    const { triggerActionsByType } = useTriggerAction(widget);
    const { isDisabled, isHidden } = useBehavior(widget);

    const value = widget.data?.value == null ? undefined : String(widget.data.value);
    const copiedText = widget.data?.messages?.copiedText;
    const iconName = widget.options?.iconName ?? AssetsSvgMap.ic_copy;
    const parentStyleNames = [widget.options?.style].filter(Boolean) as string[];
    const styleProps = getInviteLinkStyleProps(getSemanticColors(widget.version));
    const margin = widget.options?.margin;
    // CoreButton's fillContainer is a plain `width: 100%`, so side margins would push the pill past its parent.
    const sideMargins = (margin?.left ?? 0) + (margin?.right ?? 0);

    const onClickHandler = () => {
        if (value === undefined) {
            return;
        }
        triggerActionsByType(ActionActionTypeEnum.onClick, {
            playerActionOptions: {
                textForCopyToClipboard: value,
                toastMessage: copiedText,
            },
        }).then();
    };

    return (
        <CoreButtonView
            widget={widget as TCoreButtonViewProps['widget']}
            text={value}
            isDisabled={isDisabled}
            isHide={isHidden}
            onClick={onClickHandler}
            parentStyleNames={parentStyleNames}
            styleName="inviteLink"
            styleProps={{
                ...styleProps.container,
                margin,
                maxWidth: sideMargins ? `calc(100% - ${sideMargins}px)` : undefined,
            }}
        >
            <CoreTextView
                uid={'value-' + widget.uid}
                text={value}
                as="span"
                style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}
                isDisabled={isDisabled}
                parentStyleNames={parentStyleNames}
                styleName="inviteLinkValue"
                styleProps={styleProps.value}
            />
            <VisualView
                version={widget.version}
                uid={'icon-' + widget.uid}
                iconName={iconName}
                defaultIconName={AssetsSvgMap.ic_copy}
                iconVariant="simple"
                iconHierarchy="primary"
                iconWidth={24}
                iconHeight={24}
                isDisabled={isDisabled}
                parentStyleNames={parentStyleNames}
                styleName="inviteLinkIcon"
            />
        </CoreButtonView>
    );
};
